/* ============================================================================
   pdp-flower.js  (Task 0100)
   Client behaviors for the flower PDP core section (main-product-flower.liquid).
   This module does NOT reimplement variant selection, pricing authority, or
   pool math. Those stay in the theme's product-variant-selector / product-form
   / product-stock-alert components, which morph themselves on the bubbling
   "variantChange" event. This module layers the D1 batch-passport experience
   on top: gallery, pill decorations, per gram + points + ship lines, passport
   harvest swap, sticky cartbar, and the wholesale sheet.

   Money display never recomputes grams from weight. All per gram figures come
   from the server rendered data-pdpf-variants blob (physical grams via
   pool-resolve-physical). No em or en dashes anywhere in this file.

   Verification is done at the plain product URL on the dev theme after the
   product.flower template is assigned in admin (Nathan, 2026-07-13). There is
   no ?view=flower shim: the selector fetches the assigned template directly.
   ========================================================================== */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --------------------------------------------------------------------------
     Boot once per root. A PDP has exactly one flower core section.
     -------------------------------------------------------------------------- */
  function boot(root) {
    if (root.__pdpfBooted) return;
    root.__pdpfBooted = true;

    var $ = function (sel, ctx) { return (ctx || root).querySelector(sel); };
    var $$ = function (sel, ctx) {
      return Array.prototype.slice.call((ctx || root).querySelectorAll(sel));
    };

    var sectionId = root.getAttribute('data-section-id');
    var formId = root.getAttribute('data-form-id');
    var harvestOptionName = (root.getAttribute('data-harvest-option') || '').trim().toLowerCase();
    var flowerTypeOptionName = (root.getAttribute('data-flower-type-option') || 'Flower Type').trim().toLowerCase();
    var sizeOptionName = (root.getAttribute('data-size-option') || 'Bag Size').trim().toLowerCase();
    var freeShipThreshold = parseFloat(root.getAttribute('data-free-ship-threshold')) || 95;
    var moneyFormat = root.getAttribute('data-moneyfmt') || '${{amount}}';

    var labMap = parseJSON('[data-pdpf-lab]') || {};
    var variantsData = parseJSON('[data-pdpf-variants]') || [];

    function parseJSON(sel) {
      var el = root.querySelector(sel);
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }

    /* config copy read from data-cfg-* on the root (mirrors the schema) */
    function getCfg(key) {
      var attr = 'data-cfg-' + key.replace(/_/g, '-');
      return root.hasAttribute(attr) ? root.getAttribute(attr) : null;
    }

    /* ---- money formatting mirrors Shopify's money filter (cents in) ----
       Handles the four common amount placeholders including thousands
       separators, so a large JS-computed value (cartbar sum, per-gram) reads
       "$1,650.00" not "$1650.00". */
    function withCommas(intStr) {
      return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    function formatMoney(cents) {
      var whole = Math.round(cents / 100).toString();
      var twoDp = (cents / 100).toFixed(2);
      var twoDpParts = twoDp.split('.');
      // build each placeholder's value once
      var noDec = whole;                                   // amount_no_decimals
      var noDecComma = withCommas(whole);                  // amount_no_decimals_with_comma_separator
      var amount = twoDp;                                  // amount
      var amountComma = withCommas(twoDpParts[0]) + '.' + twoDpParts[1]; // amount_with_comma_separator
      return moneyFormat
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecComma)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }
    function money(cents) {
      // A store's money_format may include markup which arrived escaped
      // (&lt;span&gt;...). Decode entities first, then strip any tags, so the
      // rendered price never leaks literal &lt;span&gt; text.
      var out = formatMoney(cents);
      out = out
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
      return out.replace(/<[^>]*>/g, '').trim();
    }

    /* ---- card 588: sale display ----
       v.sr is the sale rate the server rendered for this variant (snippets/sale-rate.liquid),
       non-zero only while a sale window was on at render time. saleOn() re-checks the exact
       window from body data-sale-start / data-sale-end (the CURRENT window only, blank
       otherwise), so a page cached past the end of a window stops showing the sale on time.
       Sale cents round the discount half up per line, the way the native automatic discount
       and product-price.js do, so PDP price == checkout price. */
    var saleDs = document.body.dataset;
    var saleStart = saleDs.saleStart ? new Date(saleDs.saleStart) : null;
    var saleEnd = saleDs.saleEnd ? new Date(saleDs.saleEnd) : null;
    var saleName = (saleDs.saleName || '').trim();
    function saleOn() {
      if (!saleStart || !saleEnd || isNaN(saleStart.getTime()) || isNaN(saleEnd.getTime())) return false;
      var n = new Date();
      return n >= saleStart && n < saleEnd;
    }
    function saleRate(v) {
      var r = v ? parseInt(v.sr, 10) : 0;
      return (r > 0 && r < 100 && saleOn()) ? r : 0;
    }
    function saleCents(cents, rate) { return rate ? cents - Math.round(cents * rate / 100) : cents; }
    function unitCents(v) { return saleCents(v.price, saleRate(v)); }
    function pgCents(v) {
      if (!saleRate(v)) return v.pg_cents;
      return v.grams > 0 ? Math.round(unitCents(v) / v.grams) : 0;
    }
    function paintSaleTag(v) {
      var bar = $('.pricebar');
      if (!bar) return;
      var tag = bar.querySelector('[data-pdpf-saletag]');
      var r = saleRate(v);
      if (!r) { if (tag) tag.remove(); return; }
      if (!tag) {
        tag = document.createElement('span');
        tag.className = 'pricebar__sale';
        tag.setAttribute('data-pdpf-saletag', '');
        bar.appendChild(tag);
      }
      tag.textContent = (saleName ? saleName + ' \u00b7 ' : '') + r + '% off';
    }

    /* ---- selector element + option name mapping ---- */
    var selectorEl = $('product-variant-selector');
    var optionsData = [];
    if (selectorEl) {
      var oj = selectorEl.querySelector('[data-options-with-values]');
      if (oj) { try { optionsData = JSON.parse(oj.textContent); } catch (e) {} }
    }
    /* ================= current selection state ================= */
    function readSelection() {
      // Read the checked radio for every option row inside the selector.
      var opts = [];
      if (selectorEl) {
        optionsData.forEach(function (opt) {
          var input = selectorEl.querySelector('input[name="' + cssEsc(opt.name) + '"]:checked');
          opts.push(input ? input.value : null);
        });
      }
      return opts;
    }

    function cssEsc(str) { return (str || '').replace(/["\\]/g, '\\$&'); }

    function findVariant(opts) {
      return variantsData.find(function (v) {
        return v.options.length === opts.length &&
          v.options.every(function (o, i) { return o === opts[i]; });
      });
    }

    function currentVariant() {
      var v = findVariant(readSelection());
      if (v) return v;
      // fall back to the form's current variant id input
      var form = document.getElementById(formId);
      var input = (form && form.querySelector('[data-variant-id-input]')) ||
        root.querySelector('product-form [data-variant-id-input]');
      if (input) {
        var id = parseInt(input.value, 10);
        var byId = variantsData.find(function (x) { return x.id === id; });
        if (byId) return byId;
      }
      return variantsData[0] || null;
    }

    function currentQty() {
      var qi = $('[data-pdpf-qtyinput]');
      return qi ? Math.max(1, parseInt(qi.value, 10) || 1) : 1;
    }

    /* ---- which option index carries the harvest value ---- */
    function harvestIndex() {
      for (var i = 0; i < optionsData.length; i++) {
        if ((optionsData[i].name || '').trim().toLowerCase() === harvestOptionName) return i;
      }
      return -1;
    }
    function selectedHarvest() {
      var idx = harvestIndex();
      if (idx < 0) return '';
      var sel = readSelection();
      return sel[idx] || '';
    }

    /* ================= price bar + microlines ================= */
    function renderPricing() {
      var v = currentVariant();
      if (!v) return;
      var qty = currentQty();
      var rate = saleRate(v);                       // card 588
      var lineCents = saleCents(v.price * qty, rate);
      var wasCents = (v.compare_at && v.compare_at > v.price) ? v.compare_at : (rate ? v.price : 0);

      // price bar
      var nowEl = $('[data-pdpf-now]');
      var compareEl = $('[data-pdpf-compare]');
      var priceP = $('[data-pdpf-priceout]');
      if (nowEl) nowEl.textContent = money(unitCents(v));
      paintSaleTag(v);
      if (wasCents) {
        if (priceP) priceP.classList.add('is-sale');
        if (!compareEl && priceP) {
          compareEl = document.createElement('span');
          compareEl.className = 'pricebar__compare';
          compareEl.setAttribute('data-pdpf-compare', '');
          priceP.insertBefore(compareEl, nowEl);
        }
        if (compareEl) { compareEl.textContent = money(wasCents); compareEl.hidden = false; }
      } else {
        if (priceP) priceP.classList.remove('is-sale');
        if (compareEl) compareEl.hidden = true;
      }

      // standalone per-gram pill removed 2026-07-16 (Jacob/Inaiah meeting): the
      // redundant [data-pdpf-pergram] element is gone. Per-size per-gram lines are
      // still injected on each Bag Size row by decorateSizePills, and the sr-only
      // pricelive line still reads v.pg_cents, so pg_cents stays in the blob.

      // rewards points: REPLICATE snippets/price.liquid exactly. That snippet
      // does displayed UNIT price dollars x 5 with NO rounding and NO quantity
      // factor (e.g. $27.50 -> 137.5). Match unit basis + no round so this
      // figure never diverges from the rest of the site.
      var pointsEl = $('[data-pdpf-points]');
      if (pointsEl) {
        var pts = (unitCents(v) / 100) * 5;  // card 588: points on the price paid
        // drop insignificant trailing zeros the way Liquid's numeric output does
        var ptsStr = (Math.round(pts * 100) / 100).toString();
        pointsEl.textContent = 'Earn ' + ptsStr + ' Rogue Rewards Points';
      }

      // polite announcement
      var live = $('[data-pdpf-pricelive]');
      if (live) live.textContent = money(unitCents(v)) +
        (rate ? ', ' + rate + '% off' : '') +
        (pgCents(v) > 0 ? ', ' + money(pgCents(v)) + ' per gram' : '');

      updateAddButtonLabel(lineCents);
      updateCartbar(v, qty, lineCents);
    }

    /* enrich the reused theme add button label with the line subtotal, per D1
       ("Add to cart <middot> $40.00"). The Liquid keeps its own translation
       default; JS owns the enriched label so it updates on variant/qty change
       and after the theme's variantChange morph. Never write onto a hidden or
       sold-out add button (guard below), and never touch the loading spinner:
       product-form.js only toggles a "loading" attribute, so the separate
       .#button-loading node and the [loading] CSS state are untouched here. */
    function updateAddButtonLabel(lineCents) {
      var addBtn = root.querySelector('[data-add-button]');
      if (!addBtn || addBtn.hasAttribute('hidden')) return;
      var txt = addBtn.querySelector('.\\#button-text');
      if (!txt) return;
      txt.textContent = 'Add to cart · ' + money(lineCents);
    }

    /* ================= ship line reads the LIVE cart total ================= */
    var lastCartTotalCents = 0;
    function refreshShipLine() {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          lastCartTotalCents = cart.total_price || 0;
          lastCartCount = cart.item_count;
          paintShipLine();
        })
        .catch(function () {});
    }
    function paintShipLine() {
      var ship = $('[data-pdpf-ship]');
      if (!ship) return;
      var thresholdCents = freeShipThreshold * 100;
      if (lastCartTotalCents >= thresholdCents) {
        ship.textContent = 'Ships free. 30-day returns.';
      } else {
        var remain = thresholdCents - lastCartTotalCents;
        ship.textContent = 'Add ' + money(remain) + ' more for free shipping. 30-day returns.';
      }
    }

    /* ================= selector pill decorations ================= */
    function decorate() {
      if (!selectorEl) return;

      // tag each option-picker with a role so CSS + logic can target it
      var pickers = $$('product-option-picker', selectorEl);
      pickers.forEach(function (pk) {
        var nm = labelFor(pk).toLowerCase();
        if (nm === harvestOptionName) pk.setAttribute('data-opt-role', 'harvest');
        else if (nm === flowerTypeOptionName) pk.setAttribute('data-opt-role', 'type');
        else if (nm === sizeOptionName) pk.setAttribute('data-opt-role', 'size');
      });

      decorateTypePills();
      decorateSizePills();
      injectDifferenceToggle();
      restructureSizeGrid();
    }

    function labelFor(pk) {
      var nameEl = pk.querySelector('.\\#product-option-picker-name');
      return nameEl ? nameEl.textContent.replace(/:\s*$/, '').trim() : '';
    }
    function optionIndexFor(pk) {
      var nm = labelFor(pk).toLowerCase();
      for (var i = 0; i < optionsData.length; i++) {
        if ((optionsData[i].name || '').trim().toLowerCase() === nm) return i;
      }
      return -1;
    }

    /* (a) captions under Tops / Smalls; add the pill check glyph everywhere */
    function decorateTypePills() {
      var topsCap = getCfg('tops_caption') || 'Premium buds';
      var smallsCap = getCfg('smalls_caption') || 'Same flower, smaller buds';
      $$('product-option-picker[data-opt-role="type"] .\\#form-swatch', selectorEl).forEach(function (sw) {
        var input = sw.querySelector('input');
        var inner = sw.querySelector('.\\#form-swatch-inner');
        if (!input || !inner) return;
        ensureCheck(inner, input.value);
        if (inner.querySelector('.pill-cap')) return;
        var val = (input.value || '').toLowerCase();
        var capTxt = val === 'tops' ? topsCap : (val === 'smalls' ? smallsCap : '');
        if (capTxt) {
          var cap = document.createElement('span');
          cap.className = 'pill-cap';
          cap.textContent = capTxt;
          inner.appendChild(cap);
        }
      });
    }

    /* (c)(d) per size price + per gram lines, plus BEST VALUE badge */
    function decorateSizePills() {
      var sizePickers = $$('product-option-picker[data-opt-role="size"]', selectorEl);
      sizePickers.forEach(function (pk) {
        var sizeIdx = optionIndexFor(pk);
        if (sizeIdx < 0) return;
        var sel = readSelection();

        // build the candidate variants for the currently selected other options
        var inPlay = [];
        $$('.\\#form-swatch', pk).forEach(function (sw) {
          var input = sw.querySelector('input');
          var inner = sw.querySelector('.\\#form-swatch-inner');
          if (!input || !inner) return;
          ensureCheck(inner, input.value, true);
          var probe = sel.slice();
          probe[sizeIdx] = input.value;
          var v = findVariant(probe);
          // price line
          var prEl = ensureLine(inner, 'pill-pr');
          var pgEl = ensureLine(inner, 'pill-pg');
          if (v) {
            prEl.textContent = money(unitCents(v));
            pgEl.textContent = pgCents(v) > 0 ? money(pgCents(v)) + '/g' : '';
            inPlay.push({ inner: inner, pg: pgCents(v) });
            // card 588: the rate differs by bag size, so each pill says its own
            var offEl = inner.querySelector('.pill-off');
            var pr = saleRate(v);
            if (pr) {
              if (!offEl) offEl = ensureLine(inner, 'pill-off');
              offEl.textContent = pr + '% off';
            } else if (offEl) {
              offEl.remove();
            }
          } else {
            prEl.textContent = '';
            pgEl.textContent = '';
          }
          var old = inner.querySelector('.pill-best');
          if (old) old.remove();
        });

        // BEST VALUE badge on the lowest per gram in play
        var best = null;
        inPlay.forEach(function (c) {
          if (c.pg > 0 && (!best || c.pg < best.pg)) best = c;
        });
        if (best) {
          var badge = document.createElement('span');
          badge.className = 'pill-best';
          badge.textContent = 'Best value';
          best.inner.insertBefore(badge, best.inner.firstChild);
        }
      });
    }

    function ensureLine(inner, cls) {
      var el = inner.querySelector('.' + cls);
      if (!el) {
        el = document.createElement('span');
        el.className = cls;
        inner.appendChild(el);
      }
      return el;
    }
    // wrap the label text in a .pill-t span with a check glyph
    function ensureCheck(inner, value, center) {
      if (inner.querySelector('.pill-t')) return;
      var text = (inner.textContent || value || '').trim();
      inner.textContent = '';
      var t = document.createElement('span');
      t.className = 'pill-t';
      t.innerHTML = '<svg class="pill-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';
      var label = document.createElement('span');
      label.textContent = text;
      t.appendChild(label);
      inner.appendChild(t);
    }

    /* (b) "What is the difference?" toggle + explainer panel next to the type label */
    function injectDifferenceToggle() {
      var typePicker = $('product-option-picker[data-opt-role="type"]', selectorEl);
      if (!typePicker) return;
      var header = typePicker.querySelector('.\\#product-option-picker-header');
      var body = typePicker.querySelector('.\\#product-option-picker-body');
      if (!header || !body) return;
      if (header.querySelector('.optlink')) return;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'optlink';
      btn.textContent = 'What is the difference?';
      btn.setAttribute('aria-expanded', 'false');
      header.appendChild(btn);

      var panel = document.createElement('div');
      panel.className = 'explainer';
      var inner = document.createElement('div');
      var p = document.createElement('p');
      p.textContent = getCfg('explainer_copy') ||
        'Same strain, same lab results, different bud size. Tops are the whole premium buds off the top of the plant. Smalls are the smaller buds from the same plants at a lower price per gram.';
      inner.appendChild(p);
      panel.appendChild(inner);
      body.parentNode.insertBefore(panel, body);

      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') !== 'true';
        panel.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', String(open));
      });
    }

    /* (e) Bag Size = Option C "Merged Bulk Row" (2026-07-14).
       TEMPLATE-WIDE rule, robust for any flower product:

         - "Largest" size = the LAST size swatch in Shopify option-value order
           (values render ascending; we never parse weights).
         - If the product has 5 OR MORE size values, the old fixed 4-column grid
           stranded that last value on its own row. So we SPLIT the row: the
           retail pills (every size EXCEPT the largest) fill their own grid with
           no dead cells, and the largest value renders as a pill inside a
           "Buying in bulk" band alongside the wholesale prompt.
         - If the product has 4 OR FEWER size values, no split: the pills stay in
           a clean grid and the wholesale prompt is a full-width rung below.

       The wholesale prompt is ALWAYS a real external anchor to the wholesale page
       (new tab, rel=noopener). It never opens the on-page wholesale sheet and
       never scrolls to an on-page section.

       This runs on every decorate() pass and is fully idempotent. The theme does
       NOT re-render the size picker's inline group on variantChange (the option
       picker only updates URL / labels, the product-form only toggles button /
       sold-out state, the price component only swaps price nodes), so the moved
       largest field is NOT restored by the theme. This function therefore first
       RESTORES the picker to its canonical pre-split state (moves any prior bulk
       field back into the group, drops any prior band / rung) and only then reads
       the complete field set and re-applies the split. Repeated passes converge
       to the same DOM and the largest field is never lost. The pill price /
       per-gram / best-value decorations are applied by decorateSizePills(), which
       walks EVERY .#form-swatch (retail and moved-to-bulk alike), so moving a
       swatch node does not strip its injected lines. */
    var WHOLESALE_URL = 'https://rogueorigin.com/pages/wholesale-cbd-informational-page';

    function buildWholesaleAnchor(fullWidth) {
      var a = document.createElement('a');
      a.className = fullWidth ? 'whrung' : 'bag-whole';
      a.href = WHOLESALE_URL;
      a.target = '_blank';
      a.rel = 'noopener';
      if (fullWidth) {
        a.innerHTML =
          '<span class="whrung__t">10 lbs+? Talk wholesale</span>' +
          '<span class="whrung__d">Dedicated program, 1 oz to 100+ lbs</span>';
      } else {
        a.innerHTML =
          '<span class="bag-whole-t">10 lbs+? Talk wholesale ' +
            '<span class="bag-arrow" aria-hidden="true">&rarr;</span></span>' +
          '<span class="bag-whole-d">Dedicated program, 1 oz to 100+ lbs</span>';
      }
      return a;
    }

    function restructureSizeGrid() {
      var sizePicker = $('product-option-picker[data-opt-role="size"]', selectorEl);
      if (!sizePicker) return;
      var body = sizePicker.querySelector('.\\#product-option-picker-body');
      var group = sizePicker.querySelector('.\\#form-inline-group');
      if (!body || !group) return;

      // RESTORE to the canonical pre-split state before re-splitting. The theme
      // does NOT re-render this inline group on variantChange, so the moved
      // largest .#form-field lives on inside a prior .bag-bulk band, not back in
      // the group. Move any such field(s) back into the group (append, so the
      // largest returns to its last / ascending slot) BEFORE the band is removed,
      // otherwise removing the band would delete the moved field and the next
      // pass would read one fewer field and permanently strand the largest size.
      // The moved pill keeps the price / per-gram / Best Value decorations that
      // decorateSizePills() injected onto its inner, since only the node moves.
      var oldBand = body.querySelector('.bag-bulk');
      if (oldBand) {
        $$('.\\#form-field', oldBand).forEach(function (fld) { group.appendChild(fld); });
        oldBand.parentNode.removeChild(oldBand);
      }
      // remove any full-width wholesale rung the prior <=4 branch appended
      var oldRung = group.querySelector('.whrung');
      if (oldRung) oldRung.parentNode.removeChild(oldRung);
      group.removeAttribute('data-retail-count');

      // now read the TRUE, complete field set and apply the split fresh
      var fields = $$('.\\#form-field', group);
      var sizeCount = fields.length;
      if (!sizeCount) return;

      if (sizeCount >= 5) {
        // SPLIT: move the largest (last) field into a bulk band
        var largest = fields[fields.length - 1];
        var retailCount = sizeCount - 1;
        // let the retail grid fill its row cleanly with no stranded tile
        group.setAttribute('data-retail-count', String(retailCount));

        var band = document.createElement('div');
        band.className = 'bag-bulk';

        var eyebrow = document.createElement('div');
        eyebrow.className = 'bag-bulk-eyebrow';
        eyebrow.textContent = 'Buying in bulk';
        band.appendChild(eyebrow);

        // the largest pill keeps its swatch input, price, per-gram, and (if it is
        // the best per-gram) the Best Value badge that decorateSizePills injected
        band.appendChild(largest);

        band.appendChild(buildWholesaleAnchor(false));
        body.appendChild(band);
      } else {
        // no split: full-width wholesale rung below the clean grid
        group.appendChild(buildWholesaleAnchor(true));
      }
    }

    /* ================= passport + gallery harvest swap ================= */
    function swapHarvest() {
      var key = selectedHarvest().toLowerCase().trim();
      var data = labMap[key] || labMap[Object.keys(labMap)[0]] || null;
      if (!data) return;

      // subline harvest echo was removed (2026-07-14): the subline no longer
      // repeats the harvest value, so there is no [data-pdpf-sub-harvest] span to
      // update. The null-guard below keeps this safe if the span is ever absent.
      var subHarvest = $('[data-pdpf-sub-harvest]');
      if (subHarvest) {
        var idx = harvestIndex();
        var harvestVal = idx >= 0 ? readSelection()[idx] : '';
        if (harvestVal) subHarvest.textContent = harvestVal;
      }

      // ---- Batch Analysis cannabinoid rows: CBD and/or CBG -----------------
      // 2026-08-13, card 73 spec change B. A product can carry CBD on one harvest
      // and CBG on another, so the swap sets the figure AND shows or hides the row.
      //
      // The "above 0" rule is the same one the Liquid uses, and it is the whole
      // point of this change: cbd_percentage is REQUIRED on the lab_result
      // metaobject, so a CBG cultivar like White CBG stores a literal 0 there.
      // Testing for != null would print "CBD 0%, verified" against a COA that says
      // exactly 0. Number() is used because the metaobject serialises number_decimal
      // as a string, so "0" would otherwise be truthy.
      //
      // A row only exists in the DOM when SOME harvest has that cannabinoid above 0
      // (see the any_cbd / any_cbg gate in main-product-flower.liquid), so both
      // lookups are null-guarded and this is a no-op on the 39 CBD-only products.
      function setCannabinoidRow(rowSel, valSel, raw) {
        var row = $(rowSel);
        var show = raw != null && Number(raw) > 0;
        if (show) {
          $$(valSel).forEach(function (e) { e.textContent = raw; });
        }
        if (row) row.hidden = !show;
      }
      setCannabinoidRow('[data-pdpf-cbd-row]', '[data-pdpf-cbd]', data.cbd);
      setCannabinoidRow('[data-pdpf-cbg-row]', '[data-pdpf-cbg]', data.cbg);
      // THC
      var thc = $('[data-pdpf-thc]');
      if (thc && data.thc) thc.textContent = data.thc;
      // year chip + harvest year fact + collapsed-teaser year
      var yearChip = $('[data-pdpf-year-chip]');
      var harvestYear = $('[data-pdpf-harvest-year]');
      var teaserYear = $('[data-pdpf-teaser-year]');
      if (data.year != null) {
        if (yearChip) yearChip.textContent = data.year + ' Harvest';
        if (harvestYear) harvestYear.textContent = data.year + ', ';
        if (teaserYear) teaserYear.textContent = data.year + ' harvest';
      }
      // passport image + gallery COA slide image
      var pImg = $('[data-pdpf-passport-img]');
      var slideImg = $('[data-pdpf-coa-slide-img]');
      if (data.img) {
        if (pImg) pImg.src = data.img;
        if (slideImg) {
          slideImg.src = data.img;
          // the new COA image may have a different aspect ratio; if the COA
          // slide is the active mobile slide, re-measure the track height so it
          // tracks the new image (finding: async re-measure gap on harvest swap).
          remeasureGalleryHeight();
        }
      }
      // COA links: the passport lab image is now itself an anchor carrying
      // data-pdpf-coa-link (no lightbox), so more than one element may match.
      // Swap every COA link so the image link and the CTA link stay in sync.
      if (data.coa) {
        $$('[data-pdpf-coa-link]').forEach(function (coaLink) { coaLink.href = data.coa; });
        var slideLink = $('[data-pdpf-coa-slide-link]');
        if (slideLink) slideLink.href = data.coa;
      }
    }

    /* ================= sticky cartbar ================= */
    function updateCartbar(v, qty, lineCents) {
      var sum = $('[data-pdpf-cartbar-sum]');
      if (!sum || !v) return;
      var sel = readSelection().filter(Boolean).join(' ');
      var pgTxt = pgCents(v) > 0 ? ' ' + money(pgCents(v)) + '/g' : '';  // card 588
      sum.textContent = sel + '  ' + money(lineCents) + pgTxt;
    }

    /* wholesale sheet removed 2026-07-14: the Buying-in-bulk button now links to
       the external wholesale page, so the on-page sheet and its openSheet /
       closeSheet / focus-trap machinery were deleted. */
    /* ================= gallery carousel ================= */
    // exposed by initGallery so swapHarvest (which swaps the COA slide image src
    // on an already-loaded image, firing no listened-for load event) can force a
    // height re-measure of the active slide (both breakpoints). No-op until
    // initGallery runs and until the swapped image actually finishes loading.
    var remeasureGalleryHeight = function () {};
    /* exposed by initGallery for the .js-topassport contract. As of 2026-07-30 the
       Batch Analysis lives in gallery slide 2 instead of its own section below the
       gallery, so every "show me the lab facts" affordance drives the gallery to
       that slide (and scrolls the gallery into view) instead of scrolling the page
       down to #pdpf-passport. Returns false when there is no batch slide (product
       with no lab data), so callers can fall back or no-op. */
    var showBatchSlide = function () { return false; };
    /* assigned by initLightbox; used by showBatchSlide when the Batch Analysis card
       is living in the full screen gallery (mobile, option B). */
    var openLightboxAtKey = function () { return false; };
    /* assigned by initGallery; called after the Batch Analysis card is moved in or
       out of the inline track on a breakpoint change. */
    var refreshGallery = function () {};
    (function initGallery() {
      var track = $('.gal__track');
      if (!track) return;
      var slides = $$('.gal__slide', track);
      var dots = $$('.gal__dot');
      var thumbs = $$('.gal__thumb');
      var currentIndex = 0;
      /* Navigation is by KEY, not by baked index (2026-07-30). The Batch Analysis
         card moves out of this track on mobile, which would renumber every slide
         after it. keyOf() reads the shared data-key ("m0".."mN", "batch"). */
      function keyOf(el) { return el && el.getAttribute ? el.getAttribute('data-key') : null; }
      function indexOfKey(k) {
        for (var i = 0; i < slides.length; i++) { if (keyOf(slides[i]) === k) return i; }
        return -1;
      }

      /* Item 3 (2026-07-13, extended to desktop 2026-07-14): the gallery height
         snaps / animates to the ACTIVE slide's own content height so a short
         image never sits in letterbox space. This now runs at BOTH breakpoints:
         desktop dropped its fixed 4/3 crop, so desktop slides render at their
         intrinsic ratio and the track height follows the active slide the same
         way mobile does (desktop just also has the thumb rail). No polling: the
         height is (re)measured only from the IO slide-change callback, image
         load events, viewport resize, and breakpoint change. mobileMQ is kept so
         the breakpoint-change listener still fires a re-measure when the column
         width (and thus intrinsic heights) changes across 1024px. */
      var mobileMQ = window.matchMedia('(max-width:1023.98px)');

      /* Measure the active slide's OWN laid-out height. Most slides are a flex
         column sized by their content (image + any card chrome). The COA slide
         (Item 1, 2026-07-13) is instead a definite 1:1 square whose height comes
         from aspect-ratio, not from content: on such a box scrollHeight reports
         only the content extent and UNDER-reports the square height whenever the
         centered content is shorter than the square. getBoundingClientRect()
         .height (rounded) is the true laid-out box height including the
         aspect-ratio contribution, so take the MAX of the rect height and
         scrollHeight. That way the square COA slide measures as width-tall (so
         the track barely changes between a square photo and the square COA card)
         while a content-taller-than-box slide is still measured tall enough not
         to clip. Fall back to offsetHeight if both read 0. */
      function slideContentHeight(slide) {
        if (!slide) return 0;
        var rectH = 0;
        if (slide.getBoundingClientRect) {
          rectH = Math.round(slide.getBoundingClientRect().height) || 0;
        }
        return Math.max(rectH, slide.scrollHeight || 0, slide.offsetHeight || 0);
      }
      function applyTrackHeight(i) {
        var slide = slides[i];
        if (!slide) return;
        // Drives height at BOTH breakpoints now (2026-07-14): desktop dropped its
        // fixed 4/3 crop, so like mobile the track height must follow the active
        // slide's intrinsic-ratio content height.
        var h = slideContentHeight(slide);
        // fall back to the track's current rendered height so there is never a
        // zero-height flash before an image has laid out.
        if (!h) h = track.offsetHeight || 0;
        if (h) track.style.height = h + 'px';
      }

      function markCurrent(i) {
        currentIndex = i;
        var k = keyOf(slides[i]);
        dots.forEach(function (d) { d.setAttribute('aria-current', String(keyOf(d) === k)); });
        thumbs.forEach(function (t) { t.setAttribute('aria-current', String(keyOf(t) === k)); });
        applyTrackHeight(i);
      }
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            /* indexOf can be -1 for a slide that has since been moved out of this
               track (the Batch Analysis card on mobile), so guard before marking. */
            var i = slides.indexOf(en.target);
            if (en.isIntersecting && i >= 0) markCurrent(i);
          });
        }, { root: track, threshold: 0.6 });
        slides.forEach(function (s) { io.observe(s); });
      }
      /* Scroll-settle fallback (2026-07-13): the IO above only fires markCurrent
         when a slide covers 60%+ of the track root. After Item 2 locks
         overflow-y:hidden, a slide TALLER than ~1.67x the current (short) track
         height can never reach a 0.6 vertical intersection ratio while the track
         is short, so the IO would never mark it current and applyTrackHeight
         would never grow the track, leaving very-tall media clipped at rest.
         This scroll handler picks the active slide purely from HORIZONTAL scroll
         position (which slide center is nearest the track viewport center),
         independent of any vertical ratio, and re-marks it when the scroll
         settles. Prefers the native scrollend event; falls back to a debounced
         scroll timer where scrollend is unsupported. Coalesced so it never polls. */
      function nearestSlideByScroll() {
        var center = track.scrollLeft + track.clientWidth / 2;
        var best = 0;
        var bestDist = Infinity;
        for (var n = 0; n < slides.length; n++) {
          var s = slides[n];
          var sCenter = s.offsetLeft + s.offsetWidth / 2;
          var d = Math.abs(sCenter - center);
          if (d < bestDist) { bestDist = d; best = n; }
        }
        return best;
      }
      function onScrollSettle() {
        var i = nearestSlideByScroll();
        if (i !== currentIndex) markCurrent(i);
        else applyTrackHeight(i);
      }
      if ('onscrollend' in track) {
        track.addEventListener('scrollend', onScrollSettle);
      } else {
        var settleTimer = null;
        track.addEventListener('scroll', function () {
          if (settleTimer) clearTimeout(settleTimer);
          settleTimer = setTimeout(onScrollSettle, 120);
        }, { passive: true });
      }
      function goTo(i) {
        if (!slides[i]) return;
        track.scrollTo({ left: slides[i].offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      }
      dots.concat(thumbs).forEach(function (b) {
        b.addEventListener('click', function () {
          var k = keyOf(b);
          /* on mobile the batch card is not in this track; its control routes to
             the full screen gallery instead (option B, 2026-07-30). */
          if (k === 'batch' && indexOfKey('batch') < 0) { openLightboxAtKey('batch'); return; }
          var i = indexOfKey(k);
          if (i >= 0) { goTo(i); markCurrent(i); }
        });
      });
      track.addEventListener('keydown', function (e) {
        var i = dots.findIndex(function (d) { return d.getAttribute('aria-current') === 'true'; });
        if (i < 0) i = 0;
        if (e.key === 'ArrowRight' && i < slides.length - 1) { e.preventDefault(); goTo(i + 1); }
        if (e.key === 'ArrowLeft' && i > 0) { e.preventDefault(); goTo(i - 1); }
      });

      /* lazy-loaded / late images: when a slide's image finishes loading, its
         intrinsic height is finally known. Re-measure, but only if that slide is
         the one currently shown (otherwise its offscreen change is picked up
         when the user navigates to it). */
      slides.forEach(function (slide, n) {
        var imgs = Array.prototype.slice.call(slide.querySelectorAll('img'));
        imgs.forEach(function (img) {
          if (img.complete && img.naturalHeight) return;
          img.addEventListener('load', function () {
            if (n === currentIndex) applyTrackHeight(currentIndex);
          });
        });
      });

      /* viewport resize: re-measure the active slide (widths change so intrinsic
         heights change). Event-driven, coalesced to the next frame. */
      var resizeRaf = null;
      window.addEventListener('resize', function () {
        if (resizeRaf) return;
        resizeRaf = requestAnimationFrame(function () {
          resizeRaf = null;
          applyTrackHeight(currentIndex);
        });
      });

      /* breakpoint change: the track column width changes across 1024px, so the
         active slide's intrinsic height changes; re-measure on either crossing.
         Support both modern and legacy MQL APIs. */
      function onBreakpoint() { applyTrackHeight(currentIndex); }
      if (mobileMQ.addEventListener) mobileMQ.addEventListener('change', onBreakpoint);
      else if (mobileMQ.addListener) mobileMQ.addListener(onBreakpoint);

      /* first paint: set the initial height from slide 0 once its image is
         loaded. Before that the CSS natural-flow height stands in (no zero-height
         flash). If the first image is already cached/complete, measure now. */
      var firstImg = slides[0] && slides[0].querySelector('img');
      if (firstImg && !(firstImg.complete && firstImg.naturalHeight)) {
        firstImg.addEventListener('load', function () {
          if (currentIndex === 0) applyTrackHeight(0);
        });
      }
      applyTrackHeight(0);

      /* Re-measure hook for src swaps (swapHarvest). The init-time load
         listeners were only attached to images that were still loading, so a
         COA image that was already complete at init and later gets a NEW src
         (different aspect ratio harvest) fires a load event nobody watches,
         leaving the mobile track locked to the old height. This hook, called
         after a src swap, measures the active slide now (covers a cached/instant
         swap) AND attaches a one-shot load listener to any active-slide image
         that is not yet complete so the height re-measures once the new bytes
         land. Runs at both breakpoints now that applyTrackHeight drives height
         on desktop too. */
      /* Batch Analysis slide (2026-07-30). Index is resolved from the DOM rather
         than hard-coded to 1, so it stays correct if the graft position ever moves.
         Scrolls the gallery into view first (the affordance in the FAQ band is far
         down the page), then drives the track to the slide and flashes it. */
      showBatchSlide = function () {
        var batch = track.querySelector('.gal__slide--coa');
        /* Not in this track means we are on mobile and the card is living in the
           full screen gallery (option B, 2026-07-30). Open it there. */
        if (!batch) return openLightboxAtKey('batch');
        var i = slides.indexOf(batch);
        if (i < 0) return openLightboxAtKey('batch');
        var gal = track.closest('.gal') || track;
        gal.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        goTo(i);
        markCurrent(i);
        if (!reducedMotion) {
          batch.classList.remove('passport-flash'); void batch.offsetWidth;
          batch.classList.add('passport-flash');
          setTimeout(function () { batch.classList.remove('passport-flash'); }, 650);
        }
        return true;
      };

      /* Called after the Batch Analysis card is moved in or out of this track on a
         breakpoint change. Re-collects the slide list, keeps the currently shown
         slide shown where possible, and re-measures the track height. */
      refreshGallery = function () {
        var prevKey = keyOf(slides[currentIndex]);
        slides = $$('.gal__slide', track);
        var i = prevKey ? indexOfKey(prevKey) : -1;
        if (i < 0) i = 0;
        currentIndex = i;
        track.scrollTo({ left: slides[i] ? slides[i].offsetLeft : 0, behavior: 'auto' });
        markCurrent(i);
      };

      remeasureGalleryHeight = function () {
        applyTrackHeight(currentIndex);
        var slide = slides[currentIndex];
        if (!slide) return;
        Array.prototype.slice.call(slide.querySelectorAll('img')).forEach(function (img) {
          if (img.complete && img.naturalHeight) return;
          var once = function () {
            img.removeEventListener('load', once);
            applyTrackHeight(currentIndex);
          };
          img.addEventListener('load', once);
        });
      };
    })();

    /* ================= full size photo gallery (lightbox) =================
       2026-07-30, change 3 of the 2026-07-28 Inaiah review. Tapping an inline
       product photo (or its expand button, or Enter/Space on that button) opens
       a full screen swipeable gallery at that photo. Prerequisite for the mobile
       above-the-fold restructure, where the inline image shrinks to about half
       the screen width.

       Contains PRODUCT PHOTOS ONLY, so lightbox indices are MEDIA indices. The
       inline slides carry [data-pdpf-media-index]; the Batch Analysis slide has
       none and is therefore not a trigger (it has its own COA PDF link).

       Swiping is native: the track is scroll-snap, same mechanic as the inline
       gallery, so no drag handling of our own. */
    (function initLightbox() {
      var lb = $('[data-pdpf-lightbox]');
      var track = $('[data-pdpf-lb-track]');
      if (!lb || !track) return;
      /* Re-queried rather than captured: on mobile the Batch Analysis card is moved
         INTO this track (option B, 2026-07-30), so the slide list changes at the
         breakpoint. Everything below reads slides() fresh. */
      function slidesOf() { return $$('.lb__slide, .gal__slide', track); }
      var slides = slidesOf();
      var dots = $$('.lb__dot', lb);
      var counter = $('[data-pdpf-lb-i]', lb);
      var counterTotal = $('[data-pdpf-lb-n]', lb);
      var closeBtn = $('.lb__close', lb);
      var lastFocus = null;
      var openIndex = 0;
      /* Scroll lock is applied as an INLINE style, not a CSS class: pdp-flower.css
         is scoped entirely under .pdpf and carries a no-html/body-rules rule, so
         the lock cannot live in the stylesheet. Previous value is saved and
         restored so we never clobber another script's overflow. */
      var prevOverflow = null;

      function mark(i) {
        openIndex = i;
        var k = slides[i] && slides[i].getAttribute('data-key');
        dots.forEach(function (d) { d.setAttribute('aria-current', String(d.getAttribute('data-key') === k)); });
        if (counter) counter.textContent = String(i + 1);
      }
      function scrollToIndex(i, smooth) {
        slides = slidesOf();
        if (!slides[i]) return;
        track.scrollTo({ left: slides[i].offsetLeft, behavior: (smooth && !reducedMotion) ? 'smooth' : 'auto' });
        mark(i);
      }
      function indexOfKey(k) {
        slides = slidesOf();
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute('data-key') === k) return i;
        }
        return -1;
      }

      function open(i) {
        slides = slidesOf();
        // total is dynamic: mobile adds the Batch Analysis card to this gallery
        if (counterTotal) counterTotal.textContent = String(slides.length);
        if (lb.hasAttribute('hidden') === false) { scrollToIndex(i, true); return; }
        lastFocus = document.activeElement;
        /* Re-parent to <body>, once, on first open. The Smile.io rewards launcher
           (#smile-ui-lite-container) sits at z-index 2147483647, the maximum, and
           is appended to <body>, so a tie on z-index is decided by DOM order. As
           the last child of <body> this overlay wins that tie. Moving it also puts
           it outside any ancestor that could clip or re-stack a fixed child. The
           element keeps its .pdpf class so the scoped tokens and rules still
           apply, and every listener bound at init survives the move. */
        if (lb.parentNode !== document.body) document.body.appendChild(lb);
        lb.removeAttribute('hidden');
        prevOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        // the track must be laid out before offsetLeft is meaningful
        requestAnimationFrame(function () {
          scrollToIndex(i, false);
          if (closeBtn) closeBtn.focus();
        });
      }
      function close() {
        if (lb.hasAttribute('hidden')) return;
        lb.setAttribute('hidden', '');
        document.documentElement.style.overflow = prevOverflow || '';
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }

      /* open triggers: the expand button, and a tap anywhere on an inline
         product photo. Delegated on the root so it survives any DOM decoration. */
      root.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('[data-pdpf-lb-open]');
        if (btn) {
          e.preventDefault();
          open(parseInt(btn.getAttribute('data-pdpf-lb-open'), 10) || 0);
          return;
        }
        // a tap on the photo itself. Ignore the Batch Analysis slide (no media
        // index) and ignore clicks that landed on the "Lab verified" chip.
        var slide = e.target.closest && e.target.closest('.gal__slide[data-pdpf-media-index]');
        if (!slide) return;
        if (e.target.closest('.js-topassport')) return;
        if (e.target.tagName !== 'IMG') return;
        open(parseInt(slide.getAttribute('data-pdpf-media-index'), 10) || 0);
      });

      dots.forEach(function (d) {
        d.addEventListener('click', function () {
          var i = indexOfKey(d.getAttribute('data-key'));
          if (i >= 0) scrollToIndex(i, true);
        });
      });

      /* used by showBatchSlide and by the inline flask control when the Batch
         Analysis card is living in here (mobile). */
      openLightboxAtKey = function (k) {
        var i = indexOfKey(k);
        if (i < 0) return false;
        open(i);
        return true;
      };
      $$('[data-pdpf-lb-close]', lb).forEach(function (b) {
        b.addEventListener('click', close);
      });

      /* keep the dots and counter honest while the customer swipes */
      function nearest() {
        var c = track.scrollLeft + track.clientWidth / 2;
        var best = 0, bestD = Infinity;
        for (var n = 0; n < slides.length; n++) {
          var s = slides[n];
          var d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - c);
          if (d < bestD) { bestD = d; best = n; }
        }
        return best;
      }
      function onSettle() { var i = nearest(); if (i !== openIndex) mark(i); }
      if ('onscrollend' in track) {
        track.addEventListener('scrollend', onSettle);
      } else {
        var t = null;
        track.addEventListener('scroll', function () {
          if (t) clearTimeout(t);
          t = setTimeout(onSettle, 120);
        }, { passive: true });
      }

      /* keyboard: Escape closes, arrows page, Tab is trapped inside the dialog
         (only the close button and the track are focusable, so the trap is a
         simple two-stop cycle). */
      document.addEventListener('keydown', function (e) {
        if (lb.hasAttribute('hidden')) return;
        if (e.key === 'Escape') { e.preventDefault(); close(); return; }
        if (e.key === 'ArrowRight' && openIndex < slides.length - 1) { e.preventDefault(); scrollToIndex(openIndex + 1, true); }
        if (e.key === 'ArrowLeft' && openIndex > 0) { e.preventDefault(); scrollToIndex(openIndex - 1, true); }
        if (e.key === 'Tab') {
          var focusables = [closeBtn, track].filter(Boolean);
          if (!focusables.length) return;
          var idx = focusables.indexOf(document.activeElement);
          e.preventDefault();
          var next = e.shiftKey ? idx - 1 : idx + 1;
          if (next < 0) next = focusables.length - 1;
          if (next >= focusables.length) next = 0;
          focusables[next].focus();
        }
      });
    })();

    /* ================= Batch Analysis placement by breakpoint =================
       2026-07-30, option B. Changes 1 and 2 of the 2026-07-28 review pull in
       opposite directions: change 1 put the Batch Analysis inside the gallery,
       change 2 shrinks that gallery to about half the screen on mobile, where the
       batch facts would be unreadable at roughly 190px wide.

       Resolution: ONE node, re-parented on the breakpoint.
         desktop (>=1024): inline gallery, slide 2, exactly as approved.
         mobile  (<1024):  moved into the full screen gallery, where it renders at
                           full viewport width and stays readable.
       A single moved node rather than a clone, because a clone would duplicate
       #pdpf-batch and every [data-pdpf-*] lab hook, and swapHarvest resolves those
       with querySelector (first match wins), so the harvest swap would silently
       start updating the wrong copy.

       On mobile the batch card goes to the END of the full screen track, which
       keeps the media-index to position mapping trivially correct for photos. */
    (function initBatchPlacement() {
      var inlineTrack = $('.gal__track');
      var lbTrack = $('[data-pdpf-lb-track]');
      var batch = $('#pdpf-batch');
      if (!batch || !inlineTrack || !lbTrack) return;
      var mq = window.matchMedia('(max-width:1023.98px)');
      function apply() {
        if (mq.matches) {
          if (batch.parentNode !== lbTrack) {
            lbTrack.appendChild(batch);
            refreshGallery();
          }
        } else if (batch.parentNode !== inlineTrack) {
          // back to inline slide 2, immediately after the first photo
          var first = inlineTrack.querySelector('.gal__slide');
          if (first && first.nextSibling) inlineTrack.insertBefore(batch, first.nextSibling);
          else inlineTrack.appendChild(batch);
          refreshGallery();
        }
      }
      apply();
      if (mq.addEventListener) mq.addEventListener('change', apply);
      else if (mq.addListener) mq.addListener(apply);
    })();

    /* ================= qty stepper synced to the hidden form input ================= */
    var qtyOut = $('[data-pdpf-qtyout]');
    var qtyInput = $('[data-pdpf-qtyinput]');
    function setQty(n) {
      n = Math.max(1, n);
      if (qtyInput) qtyInput.value = n;
      if (qtyOut) qtyOut.textContent = n;
      renderPricing();
    }
    var qUp = $('[data-pdpf-qtyup]'), qDown = $('[data-pdpf-qtydown]');
    if (qUp) qUp.addEventListener('click', function () { setQty(currentQty() + 1); });
    if (qDown) qDown.addEventListener('click', function () { setQty(currentQty() - 1); });

    /* ================= passport accordion REMOVED 2026-07-30 =================
       The Batch Analysis accordion (its own section under the gallery, collapsed
       behind a <button> head since 2026-07-14 / 2026-07-16) no longer exists: the
       module was moved into gallery slide 2. openPassport() and its wiring are
       gone with the markup. The .js-topassport contract below now drives the
       gallery to that slide instead. */

    /* ================= .js-topassport shared contract =================
       Kept as the shared class name so both affordances stay wired: the
       "Lab verified" receipt chip on gallery slide 1, and the inline chip in the
       Straight Answers band far down the page (sections/pdp-flower-answers.liquid).
       Behaviour changed 2026-07-30: instead of scrolling to #pdpf-passport and
       opening an accordion, both now bring the gallery into view and advance it to
       the Batch Analysis slide. */
    document.addEventListener('click', function (e) {
      var chip = e.target.closest && e.target.closest('.js-topassport');
      if (!chip) return;
      /* Ownership, replacing the old `root.contains(chip)` guard (2026-07-30).
         That guard silently killed the Straight Answers chip: sections/
         pdp-flower-answers.liquid is its OWN section, OUTSIDE the main section's
         [data-pdpf-root], so root.contains() was always false and the click did
         nothing. Verified dead on dev before this fix, not a regression from the
         Batch Analysis move. New rule: this root handles the chip when the chip
         sits inside it, or when the chip belongs to no root at all (the FAQ band)
         and this root is the one holding the gallery. Keeps multi-unit pages
         (the 0362 landing page renders several [data-pdpf-root] units) from all
         reacting to one chip. */
      var owner = chip.closest('[data-pdpf-root]');
      if (owner && owner !== root) return;
      if (!owner && !root.querySelector('.gal__track')) return;
      showBatchSlide();
    });

    /* ================= pdpf:select-options shared contract ================= */
    document.addEventListener('pdpf:select-options', function (e) {
      var opts = e.detail && e.detail.options;
      if (!opts || !selectorEl) return;
      Object.keys(opts).forEach(function (optName) {
        var val = opts[optName];
        var input = selectorEl.querySelector(
          'input[name="' + cssEsc(optName) + '"][value="' + cssEsc(val) + '"]');
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      // if the selector had no fetch to do (same variant), still broadcast
      requestAnimationFrame(broadcastVariant);
    });

    function broadcastVariant() {
      var sel = readSelection();
      var optionsObj = {};
      optionsData.forEach(function (o, i) { optionsObj[o.name] = sel[i]; });
      var v = currentVariant();
      document.dispatchEvent(new CustomEvent('pdpf:variant-changed', {
        bubbles: true,
        detail: { variantId: v ? v.id : null, options: optionsObj }
      }));
    }

    /* ================= cartbar visibility ================= */
    var atcVisible = false;
    /* Remembered from the last IntersectionObserver entry: true when the real
       add button has scrolled up out of the viewport (boundingClientRect.top is
       negative), i.e. the user is BELOW the button. The sticky cartbar shows
       only in this "scrolled past" state, never when the user is above the
       button at the top of the page. syncCartbar() (called on the re-sync paths)
       reads this remembered flag rather than re-measuring. */
    var atcAbove = false;
    /* The main add button can be absent or rendered [hidden] (display:none) in
       the sold-out / max-in-cart / notify states. In those states an
       IntersectionObserver never reports isIntersecting, so atcVisible would
       stay false and the sticky cartbar would wrongly stay up offering an
       "Add to cart" that cannot add. Treat "no purchasable add button" as a
       reason to keep the cartbar hidden. */
    function addButtonPurchasable() {
      var addBtn = root.querySelector('[data-add-button]');
      if (!addBtn) return false;
      if (addBtn.hasAttribute('hidden')) return false;
      // offsetParent is null for display:none subtrees (covers CSS-driven hides)
      if (addBtn.offsetParent === null && addBtn.getClientRects().length === 0) return false;
      return true;
    }
    function hideCartbar(force) {
      var bar = $('[data-pdpf-cartbar]');
      if (!bar) return;
      // show only once the user has scrolled DOWN past the real add button
      // (atcAbove). While the button is visible or still below the viewport,
      // keep the bar hidden. Existing guards (force, purchasable) still win.
      var hide = force || !atcAbove || atcVisible || !addButtonPurchasable();
      bar.classList.toggle('is-hidden', hide);
    }
    function syncCartbar() { hideCartbar(false); }
    (function initCartbarIO() {
      var addBtn = root.querySelector('[data-add-button]');
      var bar = $('[data-pdpf-cartbar]');
      var barAdd = $('[data-pdpf-cartbar-add]');
      if (barAdd) {
        barAdd.addEventListener('click', function () {
          // guard: never attempt a submit when there is no purchasable button
          if (!addButtonPurchasable()) return;
          var form = document.getElementById(formId);
          if (form && form.requestSubmit) form.requestSubmit();
          else if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        });
      }
      if (addBtn && 'IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          var entry = entries[0];
          atcVisible = entry.isIntersecting;
          // top < 0 means the button's top edge is above the viewport, i.e. the
          // user scrolled DOWN past it. When it is below the fold (top > 0) or
          // in view, this is false and the bar stays hidden.
          atcAbove = entry.boundingClientRect.top < 0;
          syncCartbar();
        }, { threshold: 0.4 });
        io.observe(addBtn);
      } else {
        // no observer: fall back to purchasability alone
        syncCartbar();
      }
    })();

    /* ================= post-add: confirmation + cart refresh ================= */
    var lastCartCount = null;
    function fetchCart() {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); });
    }
    var form = document.getElementById(formId);
    if (form) {
      form.addEventListener('submit', function () {
        // The theme's product-form does the AJAX add asynchronously. Rather than
        // guess a single fixed delay (which can read the cart before the add
        // resolves on a slow request), poll /cart.js a few times until the item
        // count rises past the pre-submit count, then confirm. Capping the
        // attempts keeps a failed add from polling forever; on a genuine failure
        // the theme surfaces its own [data-cart-error] and showAdded() bails.
        var baseline = lastCartCount;
        var attempts = 0;
        var maxAttempts = 8; // ~8 x 300ms = 2.4s ceiling
        function poll() {
          attempts += 1;
          fetchCart().then(function (cart) {
            lastCartTotalCents = cart.total_price || 0;
            lastCartCount = cart.item_count;
            paintShipLine();
            var advanced = baseline == null || cart.item_count > baseline;
            if (advanced || attempts >= maxAttempts) {
              showAdded();
            } else {
              setTimeout(poll, 300);
            }
          }).catch(function () {
            if (attempts < maxAttempts) setTimeout(poll, 300);
          });
        }
        setTimeout(poll, 300);
      });
    }
    function showAdded() {
      var added = $('[data-pdpf-added]');
      var txt = $('[data-pdpf-added-txt]');
      if (!added) return;
      // only show if there was no cart error surfaced by the theme form
      var err = root.querySelector('[data-cart-error]');
      if (err && err.style.display !== 'none' && err.textContent.trim()) return;
      if (txt) {
        var sel = readSelection().filter(Boolean).join(' ');
        var qty = currentQty();
        txt.textContent = (qty > 1 ? qty + ' x ' : '') + sel + ' added.';
      }
      added.classList.add('is-on');
    }
    var addedX = $('[data-pdpf-added-x]');
    if (addedX) addedX.addEventListener('click', function () {
      var added = $('[data-pdpf-added]');
      if (added) added.classList.remove('is-on');
    });

    /* ================= wire selector change events ================= */
    // native input change inside the selector: re-decorate + re-price + swap.
    if (selectorEl) {
      selectorEl.addEventListener('change', function () {
        // let the theme selector resolve + morph first, then re-apply on rAF
        requestAnimationFrame(function () {
          renderPricing();
          swapHarvest();
          decorateSizePills();
          broadcastVariant();
          syncCartbar();
        });
      });
    }

    // the theme forwards a document level variantChange after it fetches / morphs
    document.addEventListener('variantChange', function (e) {
      if (!e.detail || e.detail.sectionId !== sectionId) return;
      // the product-form re-morphs on this event, so re-apply decorations on rAF.
      // the selected pill state is handled purely by CSS via input:checked.
      requestAnimationFrame(function () {
        renderPricing();
        swapHarvest();
        decorate();
        broadcastVariant();
        // the form re-morphs on this event: the add button may have just become
        // hidden (sold out / max in cart) or purchasable again. Re-sync the bar.
        syncCartbar();
      });
    });

    /* ---- window focus: refresh the live cart total ---- */
    window.addEventListener('focus', refreshShipLine);

    /* Farm Bill trust strip removed 2026-07-16 (Jacob/Inaiah meeting): the
       near-title legality line and its disclosure are gone, so nothing to wire.
       Footer covers compliance. */

    /* ================= first paint ================= */
    decorate();
    renderPricing();
    swapHarvest();
    refreshShipLine();
    broadcastVariant();
  }

  /* --------------------------------------------------------------------------
     Init: boot every flower core root on the page (there is one).
     -------------------------------------------------------------------------- */
  function init() {
    var roots = document.querySelectorAll('[data-pdpf-root]');
    Array.prototype.forEach.call(roots, boot);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
